import { useState, useEffect, useCallback } from 'react';
import { VAKOVAKO_COUNTRY_COOKIE_KEY } from '../../server/config';
import type { Region } from '../SelectRegion';
import { useRegions } from './useRegions';
import { useDetectedRegion } from '../contexts/DetectedRegionContext';

/**
 * Custom hook to manage the selected region using cookies.
 * This replaces the previous localStorage-based implementation.
 */
export function useSelectedRegion(detectedRegionCodeParam?: string): {
  selectedRegion: Region | undefined;
  setSelectedRegion: (region: Region) => void;
  effectiveRegion: Region | undefined;
} {
  const { regions } = useRegions();
  const [selectedRegion, setSelectedRegionState] = useState<Region | undefined>(undefined);
  const [isHydrated, setIsHydrated] = useState(false);

  // Get detected region from context or parameter (parameter for backward compatibility)
  const { detectedRegionCode: contextDetectedRegionCode } = useDetectedRegion();
  const detectedRegionCode = detectedRegionCodeParam ?? contextDetectedRegionCode;

  // Mark as hydrated after mount
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Get the region from cookie
  const getRegionFromCookie = useCallback((): string | undefined => {
    if (typeof window === 'undefined') return undefined;

    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === VAKOVAKO_COUNTRY_COOKIE_KEY) {
        return value;
      }
    }
    return undefined;
  }, []);

  // Get the initial region based on cookie or detected code
  const getInitialRegion = useCallback(() => {
    if (!regions || regions.length === 0) return undefined;

    // During SSR and initial hydration, use the server-provided region
    if (!isHydrated && detectedRegionCode) {
      const serverDetectedRegion = regions.find((r) => r.code === detectedRegionCode);
      if (serverDetectedRegion) return serverDetectedRegion;
    }

    // After hydration, check cookie first
    if (isHydrated) {
      const cookieRegionCode = getRegionFromCookie();
      if (cookieRegionCode) {
        const cookieRegion = regions.find((r) => r.code === cookieRegionCode);
        if (cookieRegion) return cookieRegion;
      }
    }

    // Then try the detected region code from server
    if (detectedRegionCode) {
      const serverDetectedRegion = regions.find((r) => r.code === detectedRegionCode);
      if (serverDetectedRegion) return serverDetectedRegion;
    }

    // Fallback priority: US -> CZ -> first region
    return regions.find((r) => r.code === "US") ||
           regions.find((r) => r.code === "CZ") ||
           regions[0];
  }, [regions, detectedRegionCode, getRegionFromCookie, isHydrated]);

  // Initialize the selected region
  useEffect(() => {
    const initialRegion = getInitialRegion();
    if (initialRegion && !selectedRegion) {
      setSelectedRegionState(initialRegion);
    }
  }, [getInitialRegion, selectedRegion]);

  // Set the selected region (this is already handled in SelectRegion component by setting cookie)
  const setSelectedRegion = useCallback((region: Region) => {
    setSelectedRegionState(region);
  }, []);

  // The effective region to use (never undefined if regions are available)
  const effectiveRegion = selectedRegion ?? getInitialRegion();

  return {
    selectedRegion,
    setSelectedRegion,
    effectiveRegion,
  };
}